
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import RandomCryptoTerms from "@/components/RandomCryptoTerms";
import { GraduationCap, BookOpen, ArrowRight, MessageSquare, HelpCircle, BookMarked } from "lucide-react";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import ChatOption from "@/components/ChatOption";

const KnowledgeHub = () => {
  const isMobile = useIsMobile();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F1F0FB] to-white">
      <Header />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#4A607B] mb-6 relative inline-block">
              Knowledge Hub
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-1/4 h-1 bg-corporate-secondary-100 rounded-full"></div>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mt-8">
              Welcome to Metrade's Knowledge Hub, your comprehensive resource for understanding
              the world of cryptocurrency. Whether you're new to crypto or looking to expand
              your knowledge, we've got you covered.
            </p>
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid gap-8 md:grid-cols-3 max-w-4xl mx-auto mb-16 h-full"
        >
          {/* FAQ Card */}
          <Link
            to="/faqs"
            className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 flex"
          >
            <div className="bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] p-1 w-full flex">
              <div className="bg-white p-8 h-full w-full rounded-2xl group-hover:bg-gray-50 transition-all duration-300 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#FF6B6B]/10 flex items-center justify-center mr-4 group-hover:bg-[#FF6B6B]/20 transition-colors duration-300">
                    <HelpCircle className="w-7 h-7 text-[#FF6B6B]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-[#4A607B] group-hover:text-[#FF6B6B] transition-colors">
                      Frequently Asked Questions
                    </h2>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4 flex-grow">
                  Get quick answers to common questions about cryptocurrency trading, copy trading, 
                  and our platform. Search through our comprehensive FAQ database.
                </p>
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center text-[#FF6B6B] font-medium group-hover:translate-x-1 transition-transform duration-300">
                    View FAQs <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>

          {/* Crypto Basics Card */}
          <Link
            to="/knowledge-hub/basics"
            className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 flex"
          >
            <div className="bg-gradient-to-br from-corporate-primary-75 to-corporate-primary-100 p-1 w-full flex">
              <div className="bg-white p-8 h-full w-full rounded-2xl group-hover:bg-gray-50 transition-all duration-300 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#9b87f5]/10 flex items-center justify-center mr-4 group-hover:bg-[#9b87f5]/20 transition-colors duration-300">
                    <GraduationCap className="w-7 h-7 text-[#9b87f5]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-[#4A607B] group-hover:text-[#9b87f5] transition-colors">
                      Crypto Basics
                    </h2>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4 flex-grow">
                  Learn the fundamentals of cryptocurrency, blockchain technology, and
                  digital assets through our comprehensive guides.
                </p>
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center text-[#9b87f5] font-medium group-hover:translate-x-1 transition-transform duration-300">
                    Explore <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>

          {/* Blog Card - NEW */}
          <Link
            to="/knowledge-hub/blog"
            className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 flex"
          >
            <div className="bg-gradient-to-br from-[#3498db] to-[#2980b9] p-1 w-full flex">
              <div className="bg-white p-8 h-full w-full rounded-2xl group-hover:bg-gray-50 transition-all duration-300 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#3498db]/10 flex items-center justify-center mr-4 group-hover:bg-[#3498db]/20 transition-colors duration-300">
                    <BookMarked className="w-7 h-7 text-[#3498db]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-[#4A607B] group-hover:text-[#3498db] transition-colors">
                      Blog
                    </h2>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4 flex-grow">
                  Explore our latest articles, insights, and market analyses from crypto experts and industry leaders.
                </p>
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center text-[#3498db] font-medium group-hover:translate-x-1 transition-transform duration-300">
                    Read Articles <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>

          {/* A-Z Glossary Card */}
          <Link
            to="/knowledge-hub/glossary"
            className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 flex"
          >
            <div className="bg-gradient-to-br from-corporate-secondary-75 to-corporate-secondary-100 p-1 w-full flex">
              <div className="bg-white p-8 h-full w-full rounded-2xl group-hover:bg-gray-50 transition-all duration-300 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#7E69AB]/10 flex items-center justify-center mr-4 group-hover:bg-[#7E69AB]/20 transition-colors duration-300">
                    <BookOpen className="w-7 h-7 text-[#7E69AB]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-[#4A607B] group-hover:text-[#7E69AB] transition-colors">
                      A-Z Glossary
                    </h2>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4 flex-grow">
                  Explore our comprehensive dictionary of cryptocurrency terms and 
                  definitions for better understanding.
                </p>
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center text-[#7E69AB] font-medium group-hover:translate-x-1 transition-transform duration-300">
                    Browse <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
          
          {/* Crypto Trading Quiz Card */}
          <Link
            to="/crypto-trading-quiz"
            className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 flex"
          >
            <div className="bg-gradient-to-br from-[#4CAF50] to-[#2E7D32] p-1 w-full flex">
              <div className="bg-white p-8 h-full w-full rounded-2xl group-hover:bg-gray-50 transition-all duration-300 flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#4CAF50]/10 flex items-center justify-center mr-4 group-hover:bg-[#4CAF50]/20 transition-colors duration-300">
                    <MessageSquare className="w-7 h-7 text-[#4CAF50]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold text-[#4A607B] group-hover:text-[#4CAF50] transition-colors">
                      Crypto Trading Quiz
                    </h2>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4 flex-grow">
                  Take a short quiz to assess your knowledge and receive personalized
                  strategy recommendations.
                </p>
                <div className="mt-6 flex justify-end">
                  <span className="inline-flex items-center text-[#4CAF50] font-medium group-hover:translate-x-1 transition-transform duration-300">
                    Start Quiz <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-lg"
        >
          <div className="bg-gradient-to-r from-corporate-primary-100 to-corporate-primary-75 p-0.5">
            <div className="bg-white p-8 rounded-2xl">
              <h3 className="text-2xl font-semibold text-[#4A607B] mb-6 text-center relative inline-block">
                <span className="relative z-10">Learn Crypto Terms</span>
                <div className="absolute bottom-0 left-0 h-3 w-full bg-corporate-secondary-25 -z-0"></div>
              </h3>
              <RandomCryptoTerms />
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Only show ChatOption on desktop */}
      {!isMobile && (
        <ChatOption />
      )}
      
      <Footer />
    </div>
  );
};

export default KnowledgeHub;
